/**
 * Dev Panel state management composable
 * Controls panel visibility, position, and active tab
 */
import { ref, watch, computed } from "vue";
import { isLocalEnv } from "@/utils/IdentifyGlobalEnv";

const STORAGE_KEY = "ai-assistant-dev-panel";

interface PanelState {
  isOpen: boolean;
  isMinimized: boolean;
  isExpanded: boolean;
  activeTab: string;
  position: { x: number; y: number };
  height: number;
  width: number;
}

const defaultState: PanelState = {
  isOpen: false,
  isMinimized: true,
  isExpanded: false,
  activeTab: "scenarios",
  position: { x: -1, y: -1 }, // -1 means use default position
  height: 420,
  width: 380,
};

// Load state from localStorage
function loadState(): PanelState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...defaultState, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.warn("[DevPanel] Failed to load state:", error);
  }
  return { ...defaultState };
}

// Save state to localStorage
function saveState(state: PanelState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn("[DevPanel] Failed to save state:", error);
  }
}

// Singleton state
const panelState = ref<PanelState>(loadState());

// Watch for changes and persist
watch(
  panelState,
  (newState) => {
    saveState(newState);
  },
  { deep: true }
);

export function useDevPanel() {
  // Only allow dev panel in local environment
  const isDevEnvironment = computed(() => isLocalEnv());

  const isOpen = computed({
    get: () => panelState.value.isOpen,
    set: (value: boolean) => {
      panelState.value.isOpen = value;
    },
  });

  const isMinimized = computed({
    get: () => panelState.value.isMinimized,
    set: (value: boolean) => {
      panelState.value.isMinimized = value;
    },
  });

  const activeTab = computed({
    get: () => panelState.value.activeTab,
    set: (value: string) => {
      panelState.value.activeTab = value;
    },
  });

  const position = computed({
    get: () => panelState.value.position,
    set: (value: { x: number; y: number }) => {
      panelState.value.position = value;
    },
  });

  const height = computed({
    get: () => panelState.value.height,
    set: (value: number) => {
      panelState.value.height = value;
    },
  });

  const width = computed({
    get: () => panelState.value.width,
    set: (value: number) => {
      panelState.value.width = value;
    },
  });

  const isExpanded = computed({
    get: () => panelState.value.isExpanded,
    set: (value: boolean) => {
      panelState.value.isExpanded = value;
    },
  });

  // Actions
  const togglePanel = () => {
    if (isMinimized.value) {
      isMinimized.value = false;
      isOpen.value = true;
    } else {
      isMinimized.value = true;
    }
  };

  const openPanel = () => {
    isOpen.value = true;
    isMinimized.value = false;
  };

  const closePanel = () => {
    isMinimized.value = true;
  };

  const minimizePanel = () => {
    isMinimized.value = true;
  };

  const maximizePanel = () => {
    isMinimized.value = false;
    isOpen.value = true;
  };

  const setActiveTab = (tab: string) => {
    activeTab.value = tab;
  };

  const updatePosition = (x: number, y: number) => {
    position.value = { x, y };
  };

  const updateHeight = (newHeight: number) => {
    height.value = Math.max(200, Math.min(900, newHeight));
  };

  const updateWidth = (newWidth: number) => {
    width.value = Math.max(320, Math.min(1200, newWidth));
  };

  const updateSize = (newWidth: number, newHeight: number) => {
    width.value = Math.max(320, Math.min(1200, newWidth));
    height.value = Math.max(200, Math.min(900, newHeight));
  };

  const resetPosition = () => {
    position.value = { x: -1, y: -1 };
  };

  const resetSize = () => {
    width.value = defaultState.width;
    height.value = defaultState.height;
  };

  const toggleExpanded = () => {
    isExpanded.value = !isExpanded.value;
  };

  return {
    // State
    isDevEnvironment,
    isOpen,
    isMinimized,
    isExpanded,
    activeTab,
    position,
    height,
    width,

    // Actions
    togglePanel,
    openPanel,
    closePanel,
    minimizePanel,
    maximizePanel,
    setActiveTab,
    updatePosition,
    updateHeight,
    updateWidth,
    updateSize,
    resetPosition,
    resetSize,
    toggleExpanded,
  };
}

// Available tabs
export const DEV_PANEL_TABS = [
  { id: "scenarios", label: "Scenarios", icon: "settings" },
  { id: "state", label: "State", icon: "code" },
  { id: "events", label: "Events", icon: "bell" },
  { id: "storage", label: "Storage", icon: "archive" },
  { id: "flags", label: "Flags", icon: "flag" },
] as const;

export type DevPanelTab = (typeof DEV_PANEL_TABS)[number]["id"];
